import {gql} from '@apollo/client'
import React, {useMemo} from 'react'
import {
  DropdownBody,
  DropdownFooter,
  DropdownItem
} from '../../../../components/library/dropdown/content'
import {OverflowFooter} from '../../../../components/overflow'
import {
  BusinessUserStatus,
  PermissionAction,
  PermissionResource
} from '../../../../data/workbench-api/generated/types'
import {useAnalytics} from '../../../../lib/analytics/analytics'
import {useAuth} from '../../../../lib/auth'
import {useToast} from '../../../../lib/toast'
import {OrganisationNode} from '../../../../organisation'
import {DeleteLink} from '../../../configuration/components'
import {
  MemberRowOptionsFragment,
  useDeleteMemberMutation,
  useResendInviteEmailMutation,
  useUpdateMemberStatusMutation
} from './generated/row-options'

export const RowOptions = ({
  member,
  closeDropdown,
  refetch,
  editMember,
  openInviteLinkModal,
  organisation
}: {
  member: MemberRowOptionsFragment
  closeDropdown(): void
  refetch(): void
  editMember(): void
  openInviteLinkModal(): void
  organisation: OrganisationNode
}) => {
  const {authState} = useAuth()
  const toast = useToast()
  const isMe = useMemo(() => {
    if (member.email && authState && authState.authenticated) {
      const email: string = member.email || ''
      return authState.email === email
    }
    return false
  }, [authState, member])
  const [saveStatus] = useUpdateMemberStatusMutation()
  const [deleteMember] = useDeleteMemberMutation()
  const [resendInviteEmail] = useResendInviteEmailMutation()
  const canEdit = organisation.hasPermission(
    PermissionResource.BusinessUser,
    PermissionAction.Edit
  )
  const analytics = useAnalytics()

  return (
    <>
      <DropdownBody>
        <DropdownItem>
          <a
            onClick={() => {
              editMember()
              closeDropdown()
            }}
          >
            {canEdit ? 'Edit' : 'View'} member
          </a>
        </DropdownItem>
        {!isMe && (
          <>
            {member.status === BusinessUserStatus.Inactive && (
              <DropdownItem disabled={!canEdit}>
                <a
                  onClick={async () => {
                    await saveStatus({
                      variables: {
                        inviteSecret: member.inviteSecret,
                        status: BusinessUserStatus.Active
                      }
                    })
                    toast('User enabled')
                    closeDropdown()
                    refetch()
                  }}
                >
                  Enable
                </a>
              </DropdownItem>
            )}
            {member.status === BusinessUserStatus.Active && (
              <DropdownItem disabled={!canEdit}>
                <a
                  onClick={async () => {
                    await saveStatus({
                      variables: {
                        inviteSecret: member.inviteSecret,
                        status: BusinessUserStatus.Inactive
                      }
                    })
                    analytics.track({
                      event: 'Disabled user for org',
                      properties: {
                        org_id: organisation.id,
                        category: 'admin'
                      }
                    })
                    toast('User disabled')
                    closeDropdown()
                    refetch()
                  }}
                >
                  Disable
                </a>
              </DropdownItem>
            )}
            {member.status === BusinessUserStatus.Invited && (
              <>
                <DropdownItem>
                  <a
                    onClick={async () => {
                      await resendInviteEmail({
                        variables: {
                          organisationId: member.organisationId,
                          id: member.id
                        }
                      })
                      closeDropdown()
                      toast('Email sent')
                    }}
                  >
                    Resend invite
                  </a>
                </DropdownItem>
                {!member.inviteExpired && (
                  <DropdownItem>
                    <a
                      onClick={() => {
                        openInviteLinkModal()
                        closeDropdown()
                      }}
                    >
                      Get invite link
                    </a>
                  </DropdownItem>
                )}
                <DropdownItem disabled={!canEdit}>
                  <a
                    onClick={async () => {
                      await saveStatus({
                        variables: {
                          inviteSecret: member.inviteSecret,
                          status: BusinessUserStatus.InviteCancelled
                        }
                      })
                      toast('Invite canceled')
                      closeDropdown()
                      refetch()
                    }}
                  >
                    Cancel invite
                  </a>
                </DropdownItem>
              </>
            )}
            {member.status === BusinessUserStatus.Inactive ||
            member.status === BusinessUserStatus.InviteCancelled ? (
              <DropdownItem disabled={!canEdit}>
                <DeleteLink
                  onClick={async () => {
                    if (confirm('Deletion is permanent - are you sure?')) {
                      await deleteMember({
                        variables: {
                          inviteSecret: member.inviteSecret
                        }
                      })
                      analytics.track({
                        properties: {
                          category: 'admin',
                          org_id: organisation.id
                        },
                        event: 'Deleted user from org'
                      })
                      closeDropdown()
                      refetch()
                    }
                  }}
                >
                  Delete
                </DeleteLink>
              </DropdownItem>
            ) : null}
          </>
        )}
      </DropdownBody>
      <DropdownFooter>
        <OverflowFooter created={member.created} />
      </DropdownFooter>
    </>
  )
}

RowOptions.fragments = {
  member: gql`
    fragment MemberRowOptions on BusinessUser {
      organisationId
      accountId
      created
      status
      inviteExpired
      email
      id
      inviteSecret
    }
  `
}

gql`
  mutation UpdateMemberStatus(
    $inviteSecret: String!
    $status: BusinessUserStatus!
  ) {
    updateBusinessUserByInviteSecret(
      input: {inviteSecret: $inviteSecret, patch: {status: $status}}
    ) {
      businessUser {
        ...MemberRowOptions
      }
    }
  }

  mutation DeleteMember($inviteSecret: String!) {
    deleteBusinessUserByInviteSecret(input: {inviteSecret: $inviteSecret}) {
      businessUser {
        ...MemberRowOptions
      }
    }
  }

  mutation ResendInviteEmail($organisationId: String!, $id: String!) {
    resendInviteEmail(input: {organisationId: $organisationId, id: $id}) {
      businessUser {
        ...MemberRowOptions
      }
    }
  }

  ${RowOptions.fragments.member}
`
